import styled from "@emotion/styled";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Card} from "@blueprintjs/core";
import isPropValid from "@emotion/is-prop-valid";

export const FaIcon = styled(FontAwesomeIcon)`
  color: #abb3bf;
`;

export const CardContainer = styled.div`
  font-family: "Roboto", sans-serifs;

  * {
    box-sizing: border-box;
    padding: 0;
    margin: 0;
  }
`;

type ManifestCardProps = {
  isSelected: boolean;
  isHighlighted?: boolean;
};

const shouldForwardPropForDispatchCard = (property: string) => {
  const validHtmlProperty = isPropValid(property);
  const shouldPassUpToStyledComponent = property === "interactive" || property === "elevation";
  const shouldNotPass = property === "isSelected" || property === "isHighlighted" || property === "visible";

  return (validHtmlProperty || shouldPassUpToStyledComponent) && !shouldNotPass;
};

type DispatchCardProps = {
  isSelected?: boolean;
  isHighlighted?: boolean;
  disabled?: boolean;
  visible?: boolean;
};

const DispatchCard = styled(Card, {
  shouldForwardProp(propName: string) {
    return shouldForwardPropForDispatchCard(propName);
  }
})<DispatchCardProps>`
  display: ${(props) => (props.visible !== undefined && !props.visible ? "none" : "block")};
  background-color: ${(props) => (props.isSelected ? "#e3e3fd" : "#FCFCFC")};
  border: ${(props) => (props.isSelected ? "1px solid #406AFF" : "1px solid #CFD4DF")};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${(props) => (props.isHighlighted ? "0 0 0 2px #7B9EFF" : "0px 2px 1px 0px #2c3a6e0f")};
  transition: none;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};

  &:hover:not([disabled]) {
    background-color: ${(props) => (props.isSelected ? "" : "#f6f6fe !important")};
    border: 1px solid #406aff;
    box-shadow: ${(props) => (props.isHighlighted ? "0 0 0 2px #7B9EFF" : "0px 2px 1px 0px #2c3a6e0f")};
  }
`;

export const MinimalCard = styled(DispatchCard)<ManifestCardProps>`
  width: 240px;
  height: fit-content;
`;

export const MicroCard = styled(DispatchCard)<ManifestCardProps>`
  width: 160px;
  height: fit-content;
`;

export const DetailedCard = styled(DispatchCard)<ManifestCardProps>`
  width: 320px;
  height: fit-content;
`;

export const CompactCard = styled(DispatchCard)<ManifestCardProps>`
  width: 240px;
  height: fit-content;
`;

export const CardGrid = styled.div<{isSelected: boolean; bodyHeight: number}>`
  position: relative;
  display: grid;
  grid-template-rows: ${(props) => `32px ${props.bodyHeight}px`};
  justify-items: center;
  align-items: center;
  height: 100%;
  width: 100%;

  & > *:first-child {
    background-color: ${(props) => (props.isSelected ? "#DDDEFD !important" : "#f4f4f4 !important")};
  }

  &:hover > *:first-child {
    background-color: ${(props) => (props.isSelected ? "#DDDEFD !important" : "#eff1fe !important")};
  }

  & > *:nth-child(2) {
    background-color: ${(props) => (props.isSelected ? "#DDDEFD !important" : "#ffffff !important")};
  }

  &:hover > *:nth-child(2) {
    background-color: ${(props) => (props.isSelected ? "#DDDEFD !important" : "#eff1fe !important")};
  }
`;

export const ScrollableList = styled.div<{isSelected?: boolean; hasScroll?: boolean}>`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: ${(props) => (props.isSelected ? "auto" : "hidden")};
  position: relative;
  padding-right: ${(props) => (props.isSelected && props.hasScroll ? "2px" : "4px")};

  //set width of scrollbar
  ::-webkit-scrollbar {
    width: 2px;
  }

  //set border radius for scrollbar
  ::-webkit-scrollbar-thumb {
    border-radius: 1px;
    margin: 2px;
    background-color: #16161680;
  }
`;

export const StopListContainer = styled.div`
  height: 100%;
  width: 100%;
  padding: 3px 3px 7px 7px;

  &.minimal {
    padding: 4px 7px 7px 7px;
  }
`;
